import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberInput } from '@coeldery/member-schema';
import { reasonKeyOf, uploadMemberPhoto } from '../lib/photo';

export interface TypeRow {
  code: string;
  name_zh_hk: string;
  name_zh_cn: string;
  name_en: string;
}

/** 表單資料＝會員欄位 ＋ 相片 URL（相片唔屬 `member-schema`；`decisions/012`） */
export type MemberFormData = MemberInput & { photo_url?: string | null };

const input: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const btn: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 8,
  border: '1px solid var(--color-divider)',
  background: 'var(--color-card)',
  fontSize: 'var(--font-size-tiny)',
  cursor: 'pointer',
};
const btnPrimary: CSSProperties = { ...btn, background: 'var(--color-primary)', color: 'var(--color-on-primary)' };

interface Props {
  form: MemberFormData;
  setForm: (f: MemberFormData) => void;
  types: TypeRow[];
  typeName: (code: string) => string;
  err: string;
  onSave: () => void;
  onCancel: () => void;
}

/** 會員新增／編輯表單（由 `/admin/members` 抽出，令該頁守 ≤ 200 行紅線） */
export default function MemberForm({ form, setForm, types, typeName, err, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const [photoErr, setPhotoErr] = useState('');
  const [uploading, setUploading] = useState(false);

  /** 揀圖 → 縮圖 → 上 Cloudinary（`decisions/012`）→ 記落 `photo_url`（未儲存，撳「儲存」才寫 DB） */
  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允許揀同一個檔兩次
    if (!file) return;
    setPhotoErr('');
    setUploading(true);
    const r = await uploadMemberPhoto(file);
    setUploading(false);
    if (r.ok) setForm({ ...form, photo_url: r.url });
    else setPhotoErr(t(reasonKeyOf(r.reason)));
  }
  return (
    <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
      <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label className="text-sm">
          {t('f_name')}
          <input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="text-sm">
          {t('f_type')}
          <select style={input} value={form.type_code} onChange={(e) => setForm({ ...form, type_code: e.target.value })}>
            {types.map((ty) => (
              <option key={ty.code} value={ty.code}>
                {typeName(ty.code)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t('f_company')}
          <input style={input} value={form.company ?? ''} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </label>
        <label className="text-sm">
          {t('f_status')}
          <select style={input} value={form.status ?? 'active'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {['active', 'pending', 'expired', 'disabled'].map((s) => (
              <option key={s} value={s}>
                {t(`status_${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t('f_email')}
          <input style={input} value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="text-sm">
          {t('f_phone')}
          <input style={input} value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
      </div>
      <label className="text-sm block mt-2">
        {t('f_photo')}
        <div className="flex items-center gap-3 mt-1">
          {form.photo_url ? (
            <img
              src={form.photo_url}
              alt=""
              style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <span
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--color-bg-tint)',
                display: 'inline-block',
                flex: '0 0 auto',
              }}
            />
          )}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={onPickPhoto}
            style={{ fontSize: 'var(--font-size-tiny)' }}
          />
          {uploading ? <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{t('photo_uploading')}</span> : null}
          {form.photo_url ? (
            <button type="button" style={btn} onClick={() => setForm({ ...form, photo_url: null })}>
              {t('f_photo_remove')}
            </button>
          ) : null}
        </div>
      </label>
      {photoErr && (
        <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }}>
          {photoErr}
        </p>
      )}
      {err && (
        <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }}>
          {err}
        </p>
      )}
      <div className="flex gap-2 mt-3">
        <button type="button" style={btnPrimary} onClick={onSave}>
          {t('btn_save')}
        </button>
        <button type="button" style={btn} onClick={onCancel}>
          {t('btn_cancel')}
        </button>
      </div>
    </div>
  );
}
